/**
 * EMBED_PATH_PREFIX is the studio surface that only ever renders inside a
 * customer's own page, in a cross-origin iframe.
 */
const EMBED_PATH_PREFIX = "/studio/embed";

/**
 * AnalyticsGate is everything the enablement decision reads. It is passed in
 * rather than read from `import.meta.env` so the rule stays a pure function the
 * test suite can drive directly.
 */
export interface AnalyticsGate {
  isE2E: boolean;
  isOptedOut: boolean;
  mode: string;
  pathname: string;
  token: string | undefined;
}

/**
 * shouldEnableAnalytics decides whether a surface may load PostHog at all.
 *
 * The embed exclusion is the substantive one: /studio/embed renders inside a
 * third-party page, so its visitors are the customer's audience, not ours, and
 * recording them is not a call Mountaya gets to make. Cross-origin storage
 * partitioning would also hand out a fresh anonymous id per host, so the data
 * would be noise even setting the question of consent aside. The public share
 * surface is deliberately not excluded: it is a top-level Mountaya page.
 *
 * The opt-out exclusion is what a device carries between visits. A person on an
 * excluded email domain is classified one round-trip after the SDK has already
 * started, so the decision is remembered locally and read back here: every later
 * load short-circuits before initAnalytics, and the posthog-js chunk is not even
 * fetched.
 *
 * The remaining exclusions keep synthetic traffic out of the project: an unset
 * token (which is the local default), the Playwright suite, and the Vitest run.
 */
export const shouldEnableAnalytics = ({
  isE2E,
  isOptedOut,
  mode,
  pathname,
  token,
}: AnalyticsGate): boolean => {
  if (!token) {
    return false;
  }

  if (isOptedOut) {
    return false;
  }

  if (isE2E || mode === "test") {
    return false;
  }

  return !pathname.startsWith(EMBED_PATH_PREFIX);
};
